/* eslint-disable no-undef */
/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, FormProvider } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { Autocomplete } from '@react-google-maps/api';

import RightModalMC from '../../components/RightModalMC';
import InputMC from '../../components/InputMC';
import DropdownMC from '../../components/DropdownMC';
import ButtonMC from '../../components/ButtonMC';
import ToggleSwitchMC from '../../components/ToggleSwitchMC';
import MapComponentMC from './MapComponentMC';

import { getCountryOptions, filterOption } from '../../../services/countryService';
import CustomerAddressModel from '../../../components/CustomerAddresses/CustomeraddressModel';
import { geocoderLocationInfo } from '../../../util/utils';

const debugLog = (hypothesisId, location, message, data = {}) => {
  // #region agent log
  fetch('http://127.0.0.1:7665/ingest/596c55f7-50c2-410c-b527-ec7ee0b81d6e', { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': '32cca4' }, body: JSON.stringify({ sessionId: '32cca4', runId: 'pre-fix', hypothesisId, location, message, data, timestamp: Date.now() }) }).catch(() => {});
  // #endregion
};

const ManageCustomerAddressMC = ({
  customer,
  selectedAddress,
  updateList,
  toggleModal,
  fakeAddress,
  hideNotification,
  loadLocation
}) => {
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  const formMethods = useForm();

  const countryOptions = useMemo(() => {
    const rawOptions = getCountryOptions();
    return rawOptions.map((country) => ({
      id: country.code || country.value,
      label: country.name_es || (typeof country.label === 'string' ? country.label : country.name_en) || '',
      value: country.code || country.value,
      dial_code: country.dial_code,
      name_es: country.name_es,
      name_en: country.name_en,
      code: country.code
    }));
  }, []);

  const filterCountryOption = useCallback((searchTerm) => {
    if (!searchTerm) return countryOptions;
    return countryOptions.filter((option) => {
      const value = option.id || option.value || option.code;
      const data = option;
      return filterOption({ value, data }, searchTerm);
    });
  }, [countryOptions]);

  const {
    register, handleSubmit, setValue, reset, formState: { errors }, getValues, watch
  } = formMethods;

  const watchedAddress = watch('address');
  const watchedCity = watch('city');
  const watchedState = watch('state');
  const watchedZipCode = watch('zip_code');
  const watchedSelectedCountry = watch('selected_country');

  const isFilled = (v) => v != null && v.trim() !== '';
  const allRequiredFilled =
    isFilled(watchedAddress) &&
    isFilled(watchedCity) &&
    isFilled(watchedState) &&
    isFilled(watchedSelectedCountry);

  const [saving, setSaving] = useState(false);
  const [openedMapPlace, setOpenedMapPlace] = useState(false);
  const [addressData, setAddressData] = useState({});
  const [googleMapsApiLoaded, setGoogleMapsApiLoaded] = useState(false);
  const autoCompleteRef = useRef(null);
  const selectedSuggestedAddressRef = useRef('');

  const { ref, ...rest } = register("address", { required: true, maxLength: 500 });
  register("latitude");
  register("longitude");

  const hidePacContainers = useCallback(() => {
    document.body.removeAttribute('data-address-autocomplete-focused');
    document.querySelectorAll('.pac-container').forEach((element) => {
      element.style.setProperty('display', 'none', 'important');
    });
  }, []);

  const setAddressAutocompleteFocused = useCallback((focused) => {
    if (focused) {
      document.body.setAttribute('data-address-autocomplete-focused', 'true');
      return;
    }

    setTimeout(() => {
      hidePacContainers();
    }, 200);
  }, [hidePacContainers]);

  const onLoadAutocomplete = useCallback((service) => {
    autoCompleteRef.current = service;
  }, []);

  useEffect(() => {
    if (isEmpty(selectedAddress)) {
      setValue('country_id', ENV.COUNTRY_CODE);
      const defaultCountry = countryOptions.find((opt) => opt.id === ENV.COUNTRY_CODE || opt.value === ENV.COUNTRY_CODE);
      setValue('selected_country', defaultCountry?.id || ENV.COUNTRY_CODE);
      return;
    }

    Object.keys(selectedAddress).forEach((key) => {
      if (key === 'country_id') {
        const countryOption = countryOptions.find((opt) => opt.id === selectedAddress[key] || opt.value === selectedAddress[key]);
        setValue('selected_country', countryOption?.id || selectedAddress[key]);
      } else {
        setValue(key, selectedAddress[key]);
      }
    });
  }, [selectedAddress, countryOptions, setValue]);

  const loadPlace = useCallback((coordinates, selectedSuggestedAddress = '') => {
    const geocoder = new google.maps.Geocoder();
    const newPosition = { lat: Number(coordinates.latitude), lng: Number(coordinates.longitude) };

    geocoder.geocode({ location: newPosition }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const info = geocoderLocationInfo(results);

        if (selectedSuggestedAddress) {
          info.address = selectedSuggestedAddress;
        }

        fillFormData(info);
        selectedSuggestedAddressRef.current = '';
      } else {
        console.log(t('customer.address.not_exist'));
      }
    });
  }, [t]);

  useEffect(() => {
    if (!loadLocation || !isLoaded || !googleMapsApiLoaded) return;

    if (selectedAddress && selectedAddress.latitude && selectedAddress.longitude) {
      loadPlace(selectedAddress);
    }
  }, [loadLocation, isLoaded, googleMapsApiLoaded, selectedAddress, loadPlace]);

  const onPlaceChanged = useCallback(() => {
    const autoComplete = autoCompleteRef.current;
    if (isEmpty(autoComplete) || typeof autoComplete?.getPlace !== 'function') return;

    const place = autoComplete.getPlace();
    if (!place) return;

    if (!place.geometry || !place.geometry.location) {
      alert(t('customer.address.error'));
      return;
    }

    selectedSuggestedAddressRef.current = place.formatted_address || '';

    const newPosition = {
      latitude: place.geometry.location.lat(),
      longitude: place.geometry.location.lng()
    };

    if (selectedSuggestedAddressRef.current) {
      setValue('address', selectedSuggestedAddressRef.current);
    }

    loadPlace(newPosition, selectedSuggestedAddressRef.current);
  }, [loadPlace, setValue, t]);

  const openSelectAddresModal = () => {
    debugLog('H4', 'ManageCustomerAddressMC.js:openSelectAddresModal', 'Opening map modal', {
      openedMapPlace,
      hasAddressValue: Boolean(getValues('address')),
      hasCityValue: Boolean(getValues('city')),
      hasStateValue: Boolean(getValues('state')),
      hasZipValue: Boolean(getValues('zip_code'))
    });
    setAddressData(getValues());
    setOpenedMapPlace(!openedMapPlace);
  };

  const closeSelectAddresModal = (data) => {
    debugLog('H2', 'ManageCustomerAddressMC.js:closeSelectAddresModal', 'Map modal returned data', {
      hasData: !isEmpty(data),
      keysCount: data ? Object.keys(data).length : 0,
      hasAddress: Boolean(data?.address),
      hasCity: Boolean(data?.city),
      hasState: Boolean(data?.state),
      hasZip: Boolean(data?.zip_code),
      hasLat: Boolean(data?.latitude),
      hasLng: Boolean(data?.longitude)
    });
    setOpenedMapPlace(false);
    if (!isEmpty(data)) {
      fillFormData(data);
    }
  };

  const fillFormData = (data) => {
    debugLog('H3', 'ManageCustomerAddressMC.js:fillFormData:before', 'Applying map data to form', {
      incomingKeysCount: data ? Object.keys(data).length : 0,
      incomingHasAddress: Boolean(data?.address),
      incomingHasCity: Boolean(data?.city),
      incomingHasState: Boolean(data?.state),
      incomingHasZip: Boolean(data?.zip_code),
      incomingHasCountry: Boolean(data?.country_id)
    });
    const currentValues = getValues();
    const nextValues = {
      ...currentValues,
      address: data.address || '',
      city: data.city || '',
      state: data.state || '',
      zip_code: data.zip_code || '',
      latitude: data.latitude || currentValues.latitude || '',
      longitude: data.longitude || currentValues.longitude || ''
    };

    if (data.country_id) {
      const countryOption = countryOptions.find((opt) => opt.id === data.country_id || opt.value === data.country_id);
      nextValues.selected_country = countryOption?.id || data.country_id;
    }

    reset(nextValues);
    debugLog('H5', 'ManageCustomerAddressMC.js:fillFormData:after', 'Form reset completed', {
      watchAddressFilled: Boolean(watch('address')),
      watchCityFilled: Boolean(watch('city')),
      watchStateFilled: Boolean(watch('state')),
      watchZipFilled: Boolean(watch('zip_code')),
      watchCountryFilled: Boolean(watch('selected_country'))
    });
  };

  const onSubmit = (data) => {
    const countryId = data.selected_country || null;
    const formData = {
      address: { ...data, country_id: countryId }
    };

    delete formData.address.selected_country;

    if (selectedAddress) {
      formData.id = selectedAddress.id;
    }

    if (fakeAddress) {
      updateList(formData.address);
      toggleModal();
      return;
    }

    setSaving(true);

    CustomerAddressModel.save(customer.web_id, formData).then((res) => {
      updateList(res.address);
      if (!hideNotification) showtoast(res.message);
      toggleModal();
    }).catch((error) => {
      setSaving(false);
      if (!hideNotification) showtoast(error.message || t('customer.address.save_error'));
    });
  };

  const checkGoogleMapsLoaded = () => {
    if (window.google && window.google.maps) {
      setGoogleMapsApiLoaded(true);
      setIsLoaded(true);
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (checkGoogleMapsLoaded()) return;

    const loadScript = () => {
      if (document.querySelector(`script[src="https://maps.googleapis.com/maps/api/js?key=${ENV.GOOGLE_API_KEY}&libraries=places"]`)) {
        const checkInterval = setInterval(() => {
          if (checkGoogleMapsLoaded()) {
            clearInterval(checkInterval);
          }
        }, 100);
        setTimeout(() => clearInterval(checkInterval), 10000);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${ENV.GOOGLE_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => {
        setGoogleMapsApiLoaded(true);
        setIsLoaded(true);
      };
      document.head.appendChild(script);
    };

    loadScript();
  }, []);

  useEffect(() => () => {
    autoCompleteRef.current = null;
    hidePacContainers();
  }, [hidePacContainers]);

  const handleClose = useCallback(() => {
    toggleModal(null);
  }, [toggleModal]);

  return (
    <RightModalMC
      title={t('customer.address.address')}
      isOpen={true}
      onClose={handleClose}
      overlayZIndex={10004}
      contentZIndex={10004}
    >
      {!openedMapPlace && (
        <div className="tw-flex tw-flex-col tw-h-full tw-max-h-screen tw-overflow-hidden tw-relative">
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className="tw-flex tw-flex-col tw-h-full tw-overflow-hidden">
              <div className="tw-flex-1 tw-overflow-y-auto tw-min-h-0 tw-p-6">
              {/* Address field */}
              <div className="tw-w-full tw-mb-4">
                <div className="tw-wrapper">
                  {!isLoaded && <div className="loading" />}
                  <div className="tw-relative">
                    {isLoaded && googleMapsApiLoaded ? (
                      <Autocomplete
                        onLoad={onLoadAutocomplete}
                        onPlaceChanged={onPlaceChanged}
                        className="tw-w-full"
                      >
                        <input
                          type="text"
                          id="address"
                          className="tw-block tw-rounded-xl tw-px-4 tw-pt-5 tw-pb-0 tw-w-full tw-text-default tw-bg-gray-50 tw-border-gray-200 tw-appearance-none focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 peer tw-border"
                          placeholder=""
                          {...rest}
                          onFocus={() => setAddressAutocompleteFocused(true)}
                          onBlur={(e) => {
                            setAddressAutocompleteFocused(false);
                            rest.onBlur?.(e);
                          }}
                          ref={(e) => {
                            ref(e);
                          }}
                        />
                      </Autocomplete>
                    ) : (
                      <input
                        type="text"
                        id="address"
                        className="tw-block tw-rounded-xl tw-px-4 tw-pt-5 tw-pb-0 tw-w-full tw-text-default tw-bg-gray-50 tw-border-gray-200 tw-appearance-none focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 peer tw-border"
                        placeholder=""
                        {...rest}
                        ref={(e) => {
                          ref(e);
                        }}
                      />
                    )}
                    <label
                      htmlFor="address"
                      className="tw-absolute tw-text-xs tw-text-blue-500 tw-top-[5px] tw-z-10 tw-start-4 tw-pointer-events-none"
                    >
                      {t('customer.address.address')}
                    </label>
                  </div>
                </div>
              </div>

              {/* Botón buscar en mapa */}
              <div className="tw-mb-6">
                <button type="button" onClick={openSelectAddresModal} className="tw-border tw-rounded-md tw-border-blue-400 tw-bg-white tw-px-2 tw-py-1" disabled={!googleMapsApiLoaded}>
                  <span className="tw-text-blue-400 tw-text-sm">
                    {googleMapsApiLoaded ? t('customer.address.button_map') : t('customer.address.button_map_loading')}
                  </span>
                </button>
              </div>

              {/* Description */}
              <div className="tw-w-full tw-mb-4">
                <InputMC
                  id="description"
                  name="description"
                  label={t('customer.address.description')}
                  textarea={true}
                  rows={3}
                  useFloatingLabel
                  maxLength={500}
                />
              </div>

              {/* City */}
              <div className="tw-w-full tw-mb-4">
                <InputMC
                  id="city"
                  name="city"
                  label={t('customer.address.city')}
                  useFloatingLabel
                  required={true}
                />
              </div>

              {/* State */}
              <div className="tw-w-full tw-mb-4">
                <InputMC
                  id="state"
                  name="state"
                  label={t('customer.address.state')}
                  useFloatingLabel
                  required={true}
                />
              </div>

              {/* Zip code */}
              <div className="tw-w-full tw-mb-4">
                <InputMC
                  id="zip_code"
                  name="zip_code"
                  label={t('customer.address.zip_code')}
                  useFloatingLabel
                  required={true}
                />
              </div>

              {/* Country */}
              <div className="tw-w-full tw-mb-4">
                <DropdownMC
                  id="selected_country"
                  name="selected_country"
                  label={t('customer.address.country')}
                  options={countryOptions}
                  defaultValue={watch('selected_country')}
                  filterOption={filterCountryOption}
                  useFloatingLabel
                  search
                />
                {errors.selected_country && <span className="tw-text-xs tw-text-red-500 tw-mb-1 tw-block">{errors.selected_country.message}</span>}
              </div>

              {/* Main address toggle */}
              <div className="tw-w-full tw-mb-4">
                <div className="tw-flex tw-flex-col tw-items-start tw-gap-y-2">
                  <ToggleSwitchMC
                    isOn={watch("main")}
                    handleToggle={(value) => setValue("main", value)}
                    label={t('customer.address.main_address_label')}
                    labelClassName="tw-text-sm tw-font-semibold"
                  />
                </div>
              </div>
              </div>

              {/* Footer */}
              <div className="tw-flex-shrink-0 tw-border-t tw-p-6">
                <div className="tw-flex tw-justify-between tw-gap-6">
                  <ButtonMC
                    text={t('customer.address.cancel')}
                    onClick={handleClose}
                    variant="GRAY"
                    className="tw-w-full"
                    size="medium"
                  />
                  <ButtonMC
                    text={t('customer.address.save')}
                    onClick={handleSubmit(onSubmit)}
                    variant="PRIMARY"
                    disabled={saving || !allRequiredFilled}
                    className="tw-w-full"
                    size="medium"
                  />
                </div>
              </div>
            </form>
          </FormProvider>
        </div>
      )}
      {openedMapPlace && googleMapsApiLoaded && (
        <MapComponentMC
          toggleModal={closeSelectAddresModal}
          addressData={addressData}
          renderModal={false}
        />
      )}
    </RightModalMC>
  );
};

export default ManageCustomerAddressMC;
